import React, { useState } from 'react';
import type { LanguageProperty } from 'ehs-react';
import {
  Button,
  Checkbox,
  getPageLogoUrl,
  intl,
  intlFmt,
  intlJSX,
  Maskable,
  showError
} from 'ehs-react';
import { usePortletStore } from '../App';
import type { modeText } from '../util/AppModeUtil';
import { AppModes, getAppModeFromString } from '../util/AppModeUtil';
import type { at } from '../generated/Beans';
import { saveOnboardingAgreements } from '../stores/ajax';

type OnboardingAgreement = at.sense.saf.settings.portal.onboarding.OnboardingAgreement;
type OnboardingAgreementField = at.sense.saf.settings.portal.onboarding.OnboardingAgreementField;

const topParagraphText: { top: modeText; bottom: modeText } = {
  top: {
    [AppModes.OnBoarding]: intl(
      'patientSelfOnboardingPortlet.welcomeScreen.patientOnboarding.accountCreated'
    ),
    [AppModes.DocumentShare]: intl(
      'patientSelfOnboardingPortlet.welcomeScreen.documentSharing.accountCreated'
    ),
    [AppModes.PasswordReset]: intl(
      'patientSelfOnboardingPortlet.welcomeScreen.passwordReset.startNow'
    )
  },
  bottom: {
    [AppModes.OnBoarding]: intl(
      'patientSelfOnboardingPortlet.welcomeScreen.patientOnboarding.completeTheAccount'
    ),
    [AppModes.DocumentShare]: intl(
      'patientSelfOnboardingPortlet.welcomeScreen.documentSharing.completeTheAccount'
    ),
    [AppModes.PasswordReset]: intl(
      'patientSelfOnboardingPortlet.welcomeScreen.passwordReset.deactivateAccountWarning'
    )
  }
};

function getTitle(appMode: AppModes, userName: string) {
  if (appMode === AppModes.PasswordReset) {
    return userName
      ? intlFmt(
          'patientSelfOnboardingPortlet.welcomeScreen.passwordReset.titleWithName',
          userName
        )
      : intl('patientSelfOnboardingPortlet.welcomeScreen.passwordReset.title');
  } else {
    return intl('patientSelfOnboardingPortlet.welcomeScreen.title');
  }
}

function getSubTitle(appMode: AppModes) {
  if (appMode !== AppModes.PasswordReset) {
    return intl('patientSelfOnboardingPortlet.welcomeScreen.subtitle');
  }
  return undefined;
}

function getReferenceLink(
  key: string,
  linkText: LanguageProperty,
  onClick: () => void,
  externalUrl?: string
) {
  return (
    <a
      key={key}
      className="clickable-element font-16 s-font"
      target={externalUrl ? '_blank' : '_self'}
      href={externalUrl ?? '#'}
      onClick={e => {
        e.stopPropagation();
        if (!externalUrl) {
          onClick();
        }
      }}
      rel="noreferrer"
    >
      {intl(linkText)}
    </a>
  );
}
const isCheckboxVisible = (fieldConfig: OnboardingAgreementField) => {
  return fieldConfig === 'ENABLED' || fieldConfig === 'ENABLED_AND_MANDATORY';
};
const isCheckboxMandatory = (fieldConfig: OnboardingAgreementField) => {
  return fieldConfig === 'ENABLED_AND_MANDATORY';
};

function WelcomePageComponent({
  onShowTermsAndConditionsClicked,
  onShowDataProtectionRegulationsClicked,
  onStartClicked,
  userName,
  canSaveOnboardingAgreements,
  backToLoginUrl
}: Readonly<{
  onShowTermsAndConditionsClicked: () => void;
  onShowDataProtectionRegulationsClicked: () => void;
  onStartClicked: (toggleValues: boolean[]) => void;
  userName: string;
  canSaveOnboardingAgreements: boolean;
  backToLoginUrl?: string;
}>): React.JSX.Element {
  const [
    appMode,
    externalTermsAndConditionsUrl,
    externalDataProtectionRegulationsUrl,
    onboardingAgreementConfiguration,
    createUserAccountAllowed,
    apiUrl,
    toggleValues,
    setToggleValues,
    getAgreementsToSubmit
  ]: [
    AppModes,
    string,
    string,
    OnboardingAgreement[],
    boolean,
    string,
    boolean[],
    (values: boolean[]) => void,
    () => { [key: string]: boolean }
  ] = usePortletStore(state => [
    getAppModeFromString(state.configuration.appMode),
    state.configuration.externalTermsAndConditionsUrl,
    state.configuration.externalDataProtectionRegulationsUrl,
    state.configuration.onboardingAgreementConfiguration,
    state.configuration.createUserAccountAllowed,
    state.url,
    state.agreementValues,
    state.setAgreementValues,
    state.getAgreementsToSubmit
  ]);

  const checkMandatoryFields = (toggleValuesParam: boolean[]) => {
    let result = true;
    onboardingAgreementConfiguration.forEach((field, index) => {
      if (
        field.fieldConfig === 'ENABLED_AND_MANDATORY' &&
        !toggleValuesParam[index]
      ) {
        result = false;
      }
    });
    return result;
  };

  const [loading, setLoading] = useState(false);
  const [hasAgreedConditions, setHasAgreedConditions] = useState(
    checkMandatoryFields(toggleValues)
  );

  const calcDetailedDescriptionId = (field: OnboardingAgreement) => {
    return 'id_det_desc_' + field.name.replaceAll(' ', '_');
  };

  const getCheckboxLabel = (fieldName: string) => {
    switch (fieldName) {
      case 'Terms and Conditions':
        return intlFmt(
          'patientSelfOnboardingPortlet.welcomeScreen.termsAndConditionsLabelText',
          intl(
            'patientSelfOnboardingPortlet.welcomeScreen.termsAndConditionsTitle'
          )
        );
      case 'Data Protection Regulations':
        return intlFmt(
          'patientSelfOnboardingPortlet.welcomeScreen.dataProtectionRegulationsLabelText',
          intl(
            'patientSelfOnboardingPortlet.welcomeScreen.dataProtectionRegulationsTitle'
          )
        );
      case 'Surveys':
        return intl(
          'patientSelfOnboardingPortlet.welcomeScreen.surveysLabelText'
        );
      default:
        return fieldName;
    }
  };

  const getLabelRequiredAsterisk = (fieldConfig: OnboardingAgreementField) => {
    if (isCheckboxMandatory(fieldConfig)) {
      return <span className="mandatory">*</span>;
    } else {
      return undefined;
    }
  };

  const getCheckboxLabelComponent = (field: OnboardingAgreement) => {
    const detailDescriptionId = calcDetailedDescriptionId(field);
    switch (field.name) {
      case 'Terms and Conditions':
        return (
          <>
            <div className="font-16 s-font">
              {intlJSX(
                'patientSelfOnboardingPortlet.welcomeScreen.termsAndConditionsLabelText',
                {
                  0: getReferenceLink(
                    'welcome-screen-terms-and-conditions-link',
                    'patientSelfOnboardingPortlet.welcomeScreen.termsAndConditionsTitle',
                    onShowTermsAndConditionsClicked,
                    externalTermsAndConditionsUrl
                  )
                }
              )}
              {getLabelRequiredAsterisk(field.fieldConfig)}
            </div>
            <div
              className="agreement-description font-14 s-font"
              id={detailDescriptionId}
            >
              {intl(
                'patientSelfOnboardingPortlet.welcomeScreen.termsAndConditionsDescriptionText'
              )}
            </div>
          </>
        );
      case 'Data Protection Regulations':
        return (
          <>
            <div className="font-16 s-font">
              {intlJSX(
                'patientSelfOnboardingPortlet.welcomeScreen.dataProtectionRegulationsLabelText',
                {
                  0: getReferenceLink(
                    'welcome-data-protection-regulations-link',
                    'patientSelfOnboardingPortlet.welcomeScreen.dataProtectionRegulationsTitle',
                    onShowDataProtectionRegulationsClicked,
                    externalDataProtectionRegulationsUrl
                  )
                }
              )}
              {getLabelRequiredAsterisk(field.fieldConfig)}
            </div>
            <div
              className="agreement-description font-14 s-font"
              id={detailDescriptionId}
            >
              {intl(
                'patientSelfOnboardingPortlet.welcomeScreen.dataProtectionRegulationsDescriptionText'
              )}
            </div>
          </>
        );
      case 'Surveys':
        return (
          <>
            <div className="font-16 s-font">
              {intl(
                'patientSelfOnboardingPortlet.welcomeScreen.surveysLabelText'
              )}
              {getLabelRequiredAsterisk(field.fieldConfig)}
            </div>
            <div
              className="agreement-description font-14 s-font"
              id={detailDescriptionId}
            >
              {intl(
                'patientSelfOnboardingPortlet.welcomeScreen.surveysDescriptionText'
              )}
            </div>
          </>
        );
      default:
        return undefined;
    }
  };

  const onCheckboxChange = (index: number, value: boolean) => {
    const newToggleValues = [...toggleValues];
    newToggleValues[index] = value;
    setToggleValues(newToggleValues);

    setHasAgreedConditions(checkMandatoryFields(newToggleValues));
  };

  const saveSelectedAgreements = (onSaved: () => void) => {
    if (!canSaveOnboardingAgreements) {
      onSaved();
      return;
    }

    saveOnboardingAgreements({
      url: apiUrl,
      selectedAgreements: getAgreementsToSubmit(),
      setLoading: visible => setLoading(visible)
    })
      .then(onSaved)
      .catch(showError);
  };

  const getOptionalParagraph = (
    text: string
  ): React.JSX.Element | undefined => {
    if (text && text.length > 0) {
      return <p className="font-16 s-font">{text}</p>;
    }
    return undefined;
  };

  const title = getTitle(appMode, userName);
  const subTitle = getSubTitle(appMode);
  return (
    <Maskable masked={loading} className="welcome-load-mask">
      <form>
        <div className="welcome-layout">
          <div className="welcome-logo-mobile-container">
            <a
              href={SenseConstants.logoutUrl}
              className="logo default-logo"
              title={intl('common.link.loginpage')}
              tabIndex={0}
            >
              <img
                className="welcome-logo"
                src={getPageLogoUrl()}
                alt={intl(
                  'patientSelfOnboardingPortlet.welcomeScreen.logoAltTextHealthineers'
                )}
              />
            </a>
          </div>
          <div className="welcome-header">
            <img
              className="welcome-image"
              src="/o/PatientSelfOnboardingPortlet/images/welcome_header.svg"
              role="none"
            />
          </div>
          <div className="welcome-content">
            <h1 className="font-28 center sh-font title">{title}</h1>
            {subTitle && (
              <div className="font-16 center s-font sub-title">{subTitle}</div>
            )}
            {!createUserAccountAllowed && (
              <div className="welcome-content-description">
                {getOptionalParagraph(topParagraphText.top[appMode])}
                {getOptionalParagraph(topParagraphText.bottom[appMode])}
              </div>
            )}
            <>
              {appMode !== AppModes.PasswordReset &&
                onboardingAgreementConfiguration &&
                onboardingAgreementConfiguration.map((field, index) => {
                  if (isCheckboxVisible(field.fieldConfig)) {
                    return (
                      <Checkbox
                        key={field.name}
                        required={isCheckboxMandatory(field.fieldConfig)}
                        label={getCheckboxLabel(field.name)}
                        labelComponent={getCheckboxLabelComponent(field)}
                        aria-describedby={calcDetailedDescriptionId(field)}
                        value={toggleValues[index]}
                        onChange={value => onCheckboxChange(index, value)}
                      />
                    );
                  }
                })}
            </>
            <div className="content-button-bar">
              <Button
                scale={'medium'}
                onClick={() => {
                  saveSelectedAgreements(() => onStartClicked(toggleValues));
                }}
                text={intl(
                  'patientSelfOnboardingPortlet.welcomeScreen.buttonStartNow'
                )}
                variant="primary"
                disabled={
                  !hasAgreedConditions && appMode !== AppModes.PasswordReset
                }
              />
            </div>
            {backToLoginUrl && (
              <div className="content-button-bar secondary">
                <Button
                  scale={'medium'}
                  onClick={() => {
                    window.location.href = backToLoginUrl;
                  }}
                  text={intl(
                    'patientSelfOnboardingPortlet.welcomeScreen.buttonBackToLogin'
                  )}
                  variant="gray"
                />
              </div>
            )}
          </div>
        </div>
      </form>
    </Maskable>
  );
}

/**
 * Render Welcome Page.
 */
export const WelcomePage = React.memo(WelcomePageComponent);
